import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { DataService } from '../services/data.service';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';
import { url } from '../config/app.constants';

/**
 * The services every authenticated guard needs.
 *
 * These MUST be resolved before the guard's first `await`. `inject()` only works inside
 * an injection context, and awaiting leaves it — a guard that injects after awaiting
 * fails at runtime with NG0203, which no amount of type checking catches because the
 * call is perfectly well typed.
 */
export interface GuardDeps {
  auth: AuthService;
  session: SessionService;
  data: DataService;
  router: Router;
}

/** Call this first, synchronously, at the top of a guard. */
export function injectGuardDeps(): GuardDeps {
  return {
    auth: inject(AuthService),
    session: inject(SessionService),
    data: inject(DataService),
    router: inject(Router),
  };
}

/**
 * Whether the signed-in account's address is verified, asking the server when the
 * session in this tab says it is not.
 *
 * The flag on the cached user is as old as the tab. An address verified from the link in
 * the mail — on a phone, in another browser — changes the account and not the tab, which
 * went on sending every navigation back to "verify your email" until the browser was
 * reloaded. So "unverified" is checked once before it is believed; a verified session
 * asks nothing. A refresh that fails (offline) leaves the answer as it was.
 */
export async function hasVerifiedEmail(auth: AuthService): Promise<boolean> {
  if (auth.currentUser?.emailVerified) {
    return true;
  }
  try {
    await auth.refreshUser();
  } catch {
    return false;
  }

  return auth.currentUser?.emailVerified === true;
}

/**
 * Shared preamble for every authenticated guard.
 *
 * Two things must happen before any authorisation decision, and getting either wrong is
 * what made the previous console's guards ineffective:
 *
 *  1. `authStateReady()` — without it, a hard refresh on a protected route reads a null
 *     `currentUser` and bounces a valid session to the login page.
 *  2. the identity slice must have loaded — deciding against a null garage list denies
 *     everyone on first paint.
 *
 * Returns null when the caller may proceed, or a UrlTree to redirect to. Takes its
 * dependencies rather than injecting them, because it is called from inside async guards.
 */
export async function requireSession(deps: GuardDeps, returnUrl?: string): Promise<UrlTree | null> {
  await deps.auth.ready();

  const user = deps.auth.currentUser;
  if (!user || user.isAnonymous) {
    return deps.router.createUrlTree([url.login.path], {
      queryParams: returnUrl ? { returnUrl } : undefined,
    });
  }
  // Unverified is not signed in yet: see `authGuard`.
  if (!(await hasVerifiedEmail(deps.auth))) {
    return deps.router.createUrlTree([url.verifyEmail.path]);
  }

  deps.session.initialize();
  await deps.session.whenReady();

  return null;
}
